import { IConfig } from '../../interfaces'
import flatTexts from '../flatTexts'
import { ADD_ALL_REASON_PRODUCTS_PLACEHOLDER } from './addAllAvailability'

/**
 * Reading and wording storefront's refusal of an all-or-nothing `prefillCart`
 * (`globals.promotions.addAllOrNothing`).
 *
 * Storefront answers a refusal with `extensions.code === '400'` (a string)
 * and `extensions.extras`: one JSON string per dropped line,
 * `{ sku, package?, reason, requested?, final? }`.
 *
 * `prefillCart` also throws a `'400'` with non-empty `extras` when the cart's
 * max-quantity checkout rule fails, but there `extras` lists rule types as
 * plain strings (`['cartConditionPendingMaxQty']`). So a refusal needs at
 * least one entry that reads as a line; anything else is not this refusal.
 */

/** The reasons that get a line of their own; anything else is "other". */
export type AllOrNothingWordedReason =
  | 'OUT_OF_STOCK'
  | 'UNVERIFIED'
  | 'QUANTITY_RULE'
  | 'NOT_AVAILABLE'
  | 'SELECTION_REQUIRED'

const WORDED_REASONS: AllOrNothingWordedReason[] = [
  'OUT_OF_STOCK',
  'UNVERIFIED',
  'QUANTITY_RULE',
  'NOT_AVAILABLE',
  'SELECTION_REQUIRED',
]

export interface IAllOrNothingRejectedLine {
  sku: string
  package: string | null
  reason: string
}

export interface IAllOrNothingRejection {
  lines: IAllOrNothingRejectedLine[]
  /** `extras` entries that were not a readable line. */
  unreadable: number
}

interface IGraphQLErrorLike {
  extensions?: { code?: unknown; extras?: unknown }
}

const readLine = (entry: unknown): IAllOrNothingRejectedLine | null => {
  let value: unknown = entry
  if (typeof entry === 'string') {
    try {
      value = JSON.parse(entry)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') return null
  const { sku, reason, package: pkg } = value as Record<string, unknown>
  if (typeof sku !== 'string' || !sku || typeof reason !== 'string') return null
  return { sku, reason, package: typeof pkg === 'string' && pkg ? pkg : null }
}

/**
 * The refusal carried by a failed all-or-nothing request, or `null` when the
 * failure is anything else. Each `extras` entry is read on its own, so one
 * unreadable entry does not cost the others their wording.
 */
export const readAllOrNothingRejection = (error: {
  graphQLErrors?: ReadonlyArray<IGraphQLErrorLike>
}): IAllOrNothingRejection | null => {
  const rejection = (error.graphQLErrors || []).find(
    ({ extensions }) =>
      extensions?.code === '400' &&
      Array.isArray(extensions.extras) &&
      extensions.extras.length > 0,
  )
  if (!rejection) return null
  const extras = rejection.extensions?.extras as unknown[]
  const lines: IAllOrNothingRejectedLine[] = []
  let unreadable = 0
  extras.forEach((entry) => {
    const line = readLine(entry)
    if (line) lines.push(line)
    else unreadable += 1
  })
  return lines.length ? { lines, unreadable } : null
}

/** What the button sent, used to name a rejected line. */
export interface IAllOrNothingRequestedLine {
  sku: string
  package?: { name?: string | null } | null
  name?: string | null
}

/**
 * A rejected line's product name: the sent line's own name, matched by sku and
 * package, then any name the page already holds for the sku, then the sku.
 * The button's lines often carry no name (gifts and deduplicated lines are
 * built with `name: ''`), and a gift storefront auto-added was never sent.
 */
const nameOf = (
  line: IAllOrNothingRejectedLine,
  requested: IAllOrNothingRequestedLine[],
  knownNames: ReadonlyMap<string, string>,
): string => {
  const match = requested.find(
    (item) =>
      item.sku === line.sku && (item.package?.name || null) === line.package,
  )
  return match?.name || knownNames.get(line.sku) || line.sku
}

const joinNames = (names: string[], locale?: string): string => {
  const unique = Array.from(new Set(names))
  try {
    return new Intl.ListFormat(locale || 'en', {
      style: 'long',
      type: 'conjunction',
    }).format(unique)
  } catch {
    return unique.join(', ')
  }
}

const TEXT_BY_REASON: Record<AllOrNothingWordedReason, string> = {
  OUT_OF_STOCK: 'addAllOutOfStockReason',
  UNVERIFIED: 'addAllRejectionUnverified',
  QUANTITY_RULE: 'addAllRejectionQuantityRule',
  NOT_AVAILABLE: 'addAllRejectionNotAvailable',
  SELECTION_REQUIRED: 'addAllChooseOptionsReason',
}

export interface IAllOrNothingMessage {
  message: string
  /** Lines worded as "other": what the report to Datadog should carry. */
  unexpected: IAllOrNothingRejectedLine[]
  /**
   * `SELECTION_REQUIRED` lines. They get a worded line, but are still
   * reported: the button cannot send a bundle's options, and the count of
   * combos refused for that sizes the work to let it.
   */
  selectionRequired: IAllOrNothingRejectedLine[]
}

/**
 * The shopper's message: an opening line, one line per worded reason naming
 * its products, the "other" line when anything was not worded, and a closing
 * line saying nothing was added. Lines with an empty text are skipped, so a
 * storefront can blank one without leaving a stray placeholder.
 */
export const buildAllOrNothingMessage = ({
  rejection,
  requested,
  config,
  knownNames = new Map(),
}: {
  rejection: IAllOrNothingRejection
  requested: IAllOrNothingRequestedLine[]
  config?: IConfig
  knownNames?: ReadonlyMap<string, string>
}): IAllOrNothingMessage => {
  const texts = flatTexts(config?.texts?.promotions)
  const locale = config?.options?.localization
  const parts: string[] = [texts.addAllRejectionOpening || '']

  WORDED_REASONS.forEach((reason) => {
    const names = rejection.lines
      .filter((line) => line.reason === reason)
      .map((line) => nameOf(line, requested, knownNames))
    const template = texts[TEXT_BY_REASON[reason]] || ''
    if (names.length && template) {
      parts.push(
        template
          .split(ADD_ALL_REASON_PRODUCTS_PLACEHOLDER)
          .join(joinNames(names, locale)),
      )
    }
  })

  const unexpected = rejection.lines.filter(
    (line) =>
      !WORDED_REASONS.includes(line.reason as AllOrNothingWordedReason),
  )
  if (unexpected.length || rejection.unreadable) {
    parts.push(texts.addAllRejectionOther || '')
  }
  parts.push(texts.addAllRejectionClosing || '')

  return {
    message: parts.filter(Boolean).join(' '),
    unexpected,
    selectionRequired: rejection.lines.filter(
      (line) => line.reason === 'SELECTION_REQUIRED',
    ),
  }
}

/**
 * The message for an all-or-nothing request that failed without a readable
 * refusal: a network error, a 5xx, the max-quantity rule, an old storefront,
 * an add-on tenant. Only a refusal proves nothing was written; storefront
 * saves an accepted cart before answering, so a lost response can follow a
 * write. This says the outcome is unknown rather than that the cart is
 * unchanged, so the shopper checks before a retry adds the combo twice.
 */
export const buildAllOrNothingUnknownOutcomeMessage = (
  config?: IConfig,
): string => flatTexts(config?.texts?.promotions).addAllOutcomeUnknown || ''
